import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, XCircle } from 'lucide-react';
import BottomSheet from '../../../../components/ui/BottomSheet';
import { cn } from '../../../../lib/utils';
import { fmtValue } from '../../../broker/bookUtils';

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50 last:border-0">
      <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</span>
      <span className="text-xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

// One entry per backend `proposed_action` — drives the modal's copy, icon, and confirm styling.
// MARK_CLOSED/MARK_CLOSED_REVERSED share danger styling (both close the tracked position);
// UPDATE_QUANTITY uses the solid-blue Sync accent (matches the row Sync button, not destructive).
const VARIANT_META = {
  MARK_CLOSED: {
    title: 'Position not found at broker',
    description: 'The broker Position Book reports this position as flat. Confirm to mark it closed in AlgoSys.',
    icon: XCircle,
    iconCls: 'text-bear',
    confirmLabel: 'Mark Closed',
    confirmCls: 'bg-bear/15 border-bear/40 text-bear hover:bg-bear/25',
  },
  MARK_CLOSED_REVERSED: {
    title: 'Position direction changed',
    description: 'The broker now holds the opposite direction for this symbol. AlgoSys can only mark the tracked position closed — it will not manage the external reverse position.',
    icon: AlertTriangle,
    iconCls: 'text-warn',
    confirmLabel: 'Mark Closed',
    confirmCls: 'bg-bear/15 border-bear/40 text-bear hover:bg-bear/25',
  },
  UPDATE_QUANTITY: {
    title: 'Quantity mismatch detected',
    description: 'The broker Position Book quantity differs from AlgoSys. Confirm the revised quantity to update AlgoSys.',
    icon: AlertTriangle,
    iconCls: 'text-warn',
    confirmLabel: 'Update Quantity',
    confirmCls: 'bg-info/15 border-info/40 text-info hover:bg-info/25',
  },
};

// ctx: { prepared, row, clientRequestId } — `prepared` is the row result from a broker Sync PREPARE
// call (CONFIRM_CLOSE / CONFIRM_QUANTITY_UPDATE / CONFIRM_REVERSED_CLOSE). CONFIRM sends the token,
// proposed_action, and expected quantities back exactly as received; this modal never reconstructs
// or alters those values — only `revised_quantity` (user input) is added for UPDATE_QUANTITY.
export default function ConfirmBrokerSyncModal({ ctx, submitting = false, error = null, onConfirm, onCancel }) {
  const prepared = ctx?.prepared || null;
  const action = String(prepared?.proposed_action || '').toUpperCase();
  const meta = VARIANT_META[action] || VARIANT_META.MARK_CLOSED;
  const isQuantityUpdate = action === 'UPDATE_QUANTITY';

  const maxQty = Math.abs(Number(prepared?.proposed_quantity ?? 0)) || 0;
  const [quantity, setQuantity] = useState(() => (maxQty > 0 ? String(maxQty) : ''));
  const inputRef = useRef(null);

  useEffect(() => {
    if (isQuantityUpdate) inputRef.current?.focus();
    // Focus once, on mount only — this modal is remounted fresh per PREPARE result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!prepared) return null;

  const qtyNum = Number(quantity);
  const qtyValid = quantity !== '' && Number.isInteger(qtyNum) && qtyNum > 0 && qtyNum <= maxQty;
  const confirmDisabled = submitting || (isQuantityUpdate && !qtyValid);

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm(isQuantityUpdate ? qtyNum : null);
  };

  const handleQuantityChange = (e) => {
    // Digits only — matches the backend's positive-integer contract (no decimals/negatives).
    const next = e.target.value.replace(/[^0-9]/g, '');
    setQuantity(next);
  };

  const Icon = meta.icon;
  const localQty = prepared.local_quantity ?? (prepared.local_signed_quantity != null ? Math.abs(prepared.local_signed_quantity) : null);
  const brokerQty = prepared.broker_quantity ?? (prepared.broker_signed_quantity != null ? Math.abs(prepared.broker_signed_quantity) : null);

  const body = (
    <>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className={meta.iconCls} />
        <h3 className="font-display font-semibold text-txt-primary">{meta.title}</h3>
      </div>
      <p className="text-xs font-mono text-txt-secondary mt-1 mb-4">{meta.description}</p>

      <div className="mb-2">
        <Field label="Symbol" value={prepared.symbol} />
        {prepared.local_side && <Field label="Local Side" value={prepared.local_side} />}
        {localQty != null && <Field label="Local Quantity" value={fmtValue(localQty)} />}
        {brokerQty != null && <Field label="Broker Quantity" value={fmtValue(brokerQty)} />}
      </div>

      {isQuantityUpdate && (
        <div className="mb-3">
          <label htmlFor="broker-sync-revised-qty" className="text-2xs font-mono uppercase tracking-widest text-txt-label block mb-1">
            Revised Quantity
          </label>
          <input
            id="broker-sync-revised-qty"
            ref={inputRef}
            type="text"
            inputMode="numeric"
            aria-invalid={!qtyValid}
            aria-describedby="broker-sync-qty-hint"
            value={quantity}
            onChange={handleQuantityChange}
            className={cn(
              'input-field w-full text-base lg:text-xs !h-11 lg:!h-8 tabular-nums',
              !qtyValid && 'border-bear/60',
            )}
          />
          <p id="broker-sync-qty-hint" className={cn('text-2xs font-mono pt-1', qtyValid ? 'text-txt-muted' : 'text-bear')}>
            Enter a whole number greater than 0, up to {fmtValue(maxQty)}.
          </p>
        </div>
      )}

      {prepared.message && (
        <p className="text-2xs font-mono text-txt-muted mb-3">{prepared.message}</p>
      )}

      {error && (
        <div role="alert" className="rounded-md border border-bear/40 bg-bear/10 px-3 py-2.5 mb-4">
          <p className="text-xs font-mono text-bear break-words">{error}</p>
        </div>
      )}

      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end gap-3 lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        <button type="button" onClick={onCancel} disabled={submitting} className="btn-ghost disabled:opacity-50 min-h-[44px] lg:min-h-0">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          className={cn(
            'font-semibold text-sm px-4 py-2 rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] lg:min-h-0',
            meta.confirmCls,
          )}
        >
          {submitting ? 'Applying…' : meta.confirmLabel}
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      {/* Desktop (>=lg): unchanged centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in shadow-panel">
          {body}
        </div>
      </div>

      {/* Mobile (<lg): bottom-sheet presentation of the same content. */}
      <BottomSheet open onClose={onCancel} title="">
        {body}
      </BottomSheet>
    </>,
    document.body,
  );
}
